import { useEffect, useCallback } from 'react';
import type { ExperienceAttributes } from '@atlassian/jira-experience-tracker';
import { usePrevious } from '@atlassian/jira-platform-react-hooks-use-previous';
import type { IssueNavigatorViewId } from '../../common/types';
import { useExperienceAnalytics } from '../../services/experience-analytics';
import { useIssueSearchQuery } from '../../services/issue-search-query';
import { useSelectedViewState } from '../selected-view-state';

type IssueSearchUIEvents = {
	/**
	 * Event to emit when the main UI content is ready and interactive. This will only be defined immediately after an
	 * issue search query has completed to signal that we are awaiting our UI components to report a successful render.
	 */
	onIssueSearchUIReady: (() => void) | undefined;
	/**
	 * Event to emit when an error occurs rendering the main UI content, either due to an error in the GraphQL response
	 * or any client-side JS errors that prevent issue results from rendering.
	 *
	 * @param error Error that prevented main UI content from rendering.
	 */
	onIssueSearchUIFail: (error: Error) => void;
};

export const useIssueSearchUIEvents = (
	location: string,
	onPageDataLoad?: (selectedView: IssueNavigatorViewId) => void,
	attributes?: ExperienceAttributes,
): IssueSearchUIEvents => {
	const [{ view }] = useSelectedViewState();
	const { onIssueSearchSuccess, onIssueSearchFail } = useExperienceAnalytics(
		onPageDataLoad,
		view,
		attributes,
	);
	const { isFetching } = useIssueSearchQuery();
	const prevIsFetching = usePrevious(isFetching);

	let onIssueSearchUIReady: (() => void) | undefined;
	if (prevIsFetching !== isFetching && !isFetching) {
		onIssueSearchUIReady = onIssueSearchSuccess;
	}

	const onIssueSearchUIFail = useCallback(
		(error: Error) => onIssueSearchFail(location, error),
		[location, onIssueSearchFail],
	);

	return {
		onIssueSearchUIReady,
		onIssueSearchUIFail,
	};
};

type BaseExperienceProps = {
	/**
	 * Analytics attributes to be emitted as part of success/fail experience events.
	 */
	attributes?: ExperienceAttributes;
	/**
	 * Event emitted when page data has loaded and the key experience is interactive.
	 */
	onPageDataLoad?: (selectedView: IssueNavigatorViewId) => void;
};

/**
 * Component to declaratively mark the issue search as successful when rendering search results to the user.
 */
// TODO Remove when cleaning up nin.page-load-instrumentation-fix
export const IssueSearchSuccess = ({ attributes, onPageDataLoad }: BaseExperienceProps) => {
	const [{ view }] = useSelectedViewState();
	const { onIssueSearchSuccess } = useExperienceAnalytics(onPageDataLoad, view, attributes);
	const { isFetching, isNetworkError } = useIssueSearchQuery();
	const prevIsFetching = usePrevious(isFetching);

	useEffect(() => {
		if (prevIsFetching !== isFetching && !isFetching && !isNetworkError) {
			onIssueSearchSuccess();
		}
	}, [isFetching, isNetworkError, onIssueSearchSuccess, prevIsFetching]);

	return null;
};

type FailExperienceProps = BaseExperienceProps & {
	/**
	 * Location the failure originated from.
	 */
	location: string;
	/**
	 * Error associated with the failure.
	 */
	error: Error;
};

/**
 * Component to declaratively mark the issue search as failed when rendering the corresponding error UI to the user.
 */
// TODO Remove when cleaning up nin.page-load-instrumentation-fix
export const IssueSearchFail = ({
	attributes,
	location,
	error,
	onPageDataLoad,
}: FailExperienceProps) => {
	const [{ view }] = useSelectedViewState();
	const { onIssueSearchFail } = useExperienceAnalytics(onPageDataLoad, view, attributes);
	const { isFetching } = useIssueSearchQuery();
	const prevIsFetching = usePrevious(isFetching);

	useEffect(() => {
		if (prevIsFetching !== isFetching && !isFetching) {
			onIssueSearchFail(location, error);
		}
	}, [error, isFetching, location, onIssueSearchFail, prevIsFetching]);

	return null;
};
